import { Button } from '@emailforge/ui';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Icon } from '@/components/ui/icons';
import { Logo } from '@/components/ui/Logo';
import { useAcceptInvitationMutation } from '@/features/members/membersApi';
import { useAppSelector } from '@/store/hooks';

export function AcceptInvitation() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const token = search.get('token');
  const user = useAppSelector((s) => s.auth.user);
  const [accept, { isLoading, error }] = useAcceptInvitationMutation();
  const [state, setState] = useState<'idle' | 'accepted' | 'error'>('idle');

  useEffect(() => {
    if (!token) return;
    if (!user) {
      // If not logged in, send them to signup with a continue param
      navigate(`/signup?invite=${token}`, { replace: true });
      return;
    }
    accept(token)
      .unwrap()
      .then(() => setState('accepted'))
      .catch(() => setState('error'));
  }, [token, user, accept, navigate]);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--paper-2)' }}>
      <div className="mf-card" style={{ padding: 40, maxWidth: 440, textAlign: 'center', boxShadow: 'var(--sh-lg)' }}>
        <Logo size={36} />
        {!token && (
          <>
            <h1 className="display" style={{ fontSize: 22, margin: '16px 0 8px' }}>
              Missing invitation token
            </h1>
            <p style={{ fontSize: 13, color: 'var(--ink-2)' }}>
              The URL seems incomplete. Ask your teammate to re-send the invitation.
            </p>
          </>
        )}
        {state === 'idle' && token && (
          <>
            <h1 className="display" style={{ fontSize: 22, margin: '16px 0 8px' }}>
              Accepting your invitation…
            </h1>
            <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
              {isLoading ? 'Working on it.' : 'Please wait.'}
            </div>
          </>
        )}
        {state === 'accepted' && (
          <>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: 'var(--spark-soft)',
                color: 'var(--spark)',
                border: '1px solid var(--spark-line)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '20px auto 12px',
              }}
            >
              <Icon.check size={20} />
            </div>
            <h1 className="display" style={{ fontSize: 24, margin: '0 0 6px' }}>
              You're in
            </h1>
            <p style={{ fontSize: 13.5, color: 'var(--ink-2)', margin: '0 0 18px' }}>
              Welcome aboard. Heading to the workspace now.
            </p>
            <Button variant="flame" onClick={() => navigate('/app')}>
              Go to workspace
            </Button>
          </>
        )}
        {state === 'error' && (
          <>
            <h1 className="display" style={{ fontSize: 22, margin: '16px 0 6px' }}>
              We couldn't accept the invitation
            </h1>
            <p style={{ fontSize: 13, color: 'var(--err)' }}>
              {(error as { data?: { message?: string } }).data?.message ?? 'Link may be expired or for a different email.'}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
